import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { PRIMARY_COLOR } from '../constants/colors';
import GenericButton from './GenericButton';
import BrandName from './BrandName';
import { getThumbnailUrl } from '../services/api';

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
  
  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const PopupContainer = styled.div`
  background: white;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  animation: slideUp 0.3s ease-out;
  font-family: 'Goudy Bookletter 1911', serif;
  
  @keyframes slideUp {
    from { 
      opacity: 0;
      transform: translateY(20px) scale(0.95);
    }
    to { 
      opacity: 1;
      transform: translateY(0) scale(1);
    }
  }
`;

const PopupHeader = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1.5rem;
  
  .icon {
    width: 48px;
    height: 48px;
    background: ${PRIMARY_COLOR};
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 1rem;
    font-size: 1.5rem;
    color: white;
  }
  
  .title {
    font-size: 1.25rem;
    font-weight: 700;
    color: #1a1a1a;
    margin: 0;
  }
`;

const PopupContent = styled.div`
  margin-bottom: 2rem;
  
  .message {
    color: #4a5568;
    font-size: 1rem;
    line-height: 1.5;
    margin-bottom: 1rem;
  }
  
  .product-info {
    padding: 1rem;
    border: 1px solid #e2e8f0;
    
    .product-name {
      font-weight: 600;
      color: #1a1a1a;
      margin-bottom: 0.5rem;
    }
    
    .product-details {
      color: #6b7280;
      font-size: 0.9rem;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: space-between;
  
  @media (max-width: 480px) {
    flex-direction: column;
    justify-content: initial;
  }
`;

const Button = styled.button`
  padding: 0.875rem 1.5rem;
  border: none;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.95rem;
  letter-spacing: -0.025em;
`;

const CartButton = styled(Button)`
  background: ${PRIMARY_COLOR};
  color: white;

  &:hover {
    background: ${PRIMARY_COLOR};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(5, 6, 5, 0.3);
  }

  @media (max-width: 480px) {
    width: 100%;
  }
`;

const CartPopup = ({ isVisible, product, onClose, onContinueShopping }) => {
  useEffect(() => {
    if (isVisible) {
      // setIsClosing(false); // Removed as per edit hint
    }
  }, [isVisible]);

  const handleClose = () => {
    // setIsClosing(true); // Removed as per edit hint
    setTimeout(() => {
      onClose();
      // setIsClosing(false); // Removed as per edit hint
    }, 300);
  };

  const handleContinueShopping = () => {
    handleClose();
    if (onContinueShopping) {
      onContinueShopping();
    }
  };

  if (!isVisible) return null;

  return (
    <PopupOverlay onClick={handleClose}>
      <PopupContainer onClick={(e) => e.stopPropagation()}>
        <PopupHeader>
          <div className="icon">✓</div>
          <h3 className="title">Sepete Eklendi!</h3>
        </PopupHeader>
        
        <PopupContent>
          {/* <p className="message">
            Ürün başarıyla sepetinize eklendi. Ne yapmak istiyorsunuz
          </p> */}
          
          {product && (
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
              <div className="product-info" style={{ flex: 1 }}>
                <div className="product-name">{product.name}</div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
                  <BrandName textAlign='left' fontSize='1rem' margin='0' />
                  {product.image && (
                    <img
                      src={getThumbnailUrl(product.image)}
                      alt={product.name}
                      style={{ width: 64, height: 64, objectFit: 'contain', borderRadius: 8, background: '#fff', marginLeft: 8 }}
                    />
                  )}
                </div>
                <div className="product-details">
                  {product.volume && `${product.volume} • `}
                  {product.price?.toLocaleString()}TL
                  {product.quantity > 1 && ` • ${product.quantity} adet`}
                </div>
              </div>
            </div>
          )}
        </PopupContent>
        
        <ButtonGroup>
          <GenericButton 
            data-cy="continue-shopping"
            onClick={handleContinueShopping}
            $fontSize="0.95rem"
            $padding="0.875rem 1.5rem"
            style={{marginBottom: '0.75rem', maxWidth: '320px', width: 'auto'}} // desktop default
          >
            Alışverişe Devam Et
          </GenericButton>
          <Link to="/cart" style={{ textDecoration: 'none' }}>
            <CartButton onClick={handleClose}>
              Sepete Git
            </CartButton>
          </Link>
        </ButtonGroup>
      </PopupContainer>
    </PopupOverlay>
  );
};

export default CartPopup;